<template>
    <div class="competitor-call-tracker">
        <div class="call-metrics" v-if="eligibleToShowCounter">
            <div class="metric">
                <span class="metric-value">{{ callCount }}</span>
                <span class="metric-label">
                    {{ translate("announcer_board.number_of_competitor_calls_to_fight") }}
                </span>
            </div>
            <div class="metric-divider"></div>
            <div class="metric">
                <span class="metric-value">{{ formattedElapsedTime }}</span>
                <span class="metric-label">{{ translate("announcer_board.time_elapsed_since_first_call") }}</span>
            </div>
        </div>
        <ToggleableButton2 @click="handleClick" :is-loading="loading" :toggled="recentlyCalled">
            <template v-slot>
                <i class="fa fa-bell"></i>
            </template>
            <template v-slot:toggled>
                <i class="fa fa-check-circle"></i>
            </template>
        </ToggleableButton2>
    </div>
</template>

<script setup lang="ts">
    import competitorCallHistoryApi from "../Api/competitorCallHistoryApi";
    import { useAnnouncerBoardStore } from "./announcerBoardStore";
    import { handleError } from "../Common/errorHandler";
    import { ref, computed, onMounted, onUnmounted } from "vue";
    import ToggleableButton2 from "../Common/ToggleableButton2.vue";
    import { delay } from "../Common/promises";
    import timeFormatter from "../Common/timeFormatter";
    import { getAnnouncerBoardCallCountUpdatesChannel } from "../Common/websocketUtils";
    import { ComplexFight } from "../Common/Fights/types";
    import { STATUS_FIGHT_IN_PROGRESS } from "../Common/consts";
    import { translate } from "../Common/translations";

    const props = defineProps<{
        fight: ComplexFight;
        eventRegistrationId: number;
    }>();

    const store = useAnnouncerBoardStore();
    const loading = ref(false);
    const recentlyCalled = ref(false);
    const currentTime = ref(Date.now());

    const competitorHistory = computed(() => {
        const fightHistories = store.competitorCallHistoryMap[props.fight.id];
        if (!fightHistories) {
            return null;
        }
        return fightHistories.find(h => h.eventRegistrationId === props.eventRegistrationId);
    });

    const callCount = computed(() => {
        return competitorHistory.value?.callCount ?? 0;
    });

    const elapsedSeconds = computed(() => {
        if (!competitorHistory.value || callCount.value === 0) {
            return 0;
        }
        const startTime = new Date(competitorHistory.value.callingStartedAt).getTime();
        return Math.floor((currentTime.value - startTime) / 1000);
    });

    const formattedElapsedTime = computed(() => {
        return timeFormatter.formatSecondsToMinutesAndSeconds(elapsedSeconds.value);
    });

    const eligibleToShowCounter = computed(() => {
        if (props.fight.id <= 0 || props.eventRegistrationId <= 0) {
            return false;
        }

        const fightStatusForMat = store.fightQueueStatuses[props.fight.matId];

        if (fightStatusForMat?.status === STATUS_FIGHT_IN_PROGRESS && fightStatusForMat?.fightId === props.fight.id) {
            return false;
        }

        return callCount.value > 0 && store.fightQueueStatuses;
    });

    const handleClick = () => {
        if (recentlyCalled.value || loading.value) {
            return;
        }

        loading.value = true;

        return competitorCallHistoryApi
            .increaseCallCountForCompetitor(store.eventId, props.fight.id, props.eventRegistrationId)
            .then(updatedMap => {
                store.websocketClient?.send(
                    {}, //No need to send any data, server will fetch the latest state
                    getAnnouncerBoardCallCountUpdatesChannel(store.eventId),
                );
                store.competitorCallHistoryMap = updatedMap;
                loading.value = false;
                recentlyCalled.value = true;
                return delay(5000);
            })
            .then(() => {
                recentlyCalled.value = false;
            })
            .catch(handleError)
            .finally(() => {
                loading.value = false;
                recentlyCalled.value = false;
            });
    };

    let interval: ReturnType<typeof setInterval> | null = null;

    onMounted(() => {
        // Update current time every second
        interval = setInterval(() => {
            currentTime.value = Date.now();
        }, 1000);
    });

    onUnmounted(() => {
        if (interval) {
            clearInterval(interval);
        }
    });
</script>

<style scoped>
    .competitor-call-tracker {
        display: inline-flex;
        align-items: center;
        gap: 12px;
    }
</style>
